import { useState, useEffect, useCallback } from 'react';
import { notificationsApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Bell,
  BellRing,
  Mail,
  MailCheck,
  Send,
  RefreshCw,
  Settings2,
  Trash2,
  AlertTriangle,
  XCircle,
  Info,
  CheckCircle2,
  Package,
  FileText,
  Database,
  ClipboardCheck,
  Plus,
  X,
  History,
  Play,
} from 'lucide-react';
import { toast } from 'sonner';

const SEVERITY_STYLE = {
  error: { icon: XCircle, color: 'text-red-600', bg: 'border-red-300 bg-red-50' },
  warning: { icon: AlertTriangle, color: 'text-amber-600', bg: 'border-amber-300 bg-amber-50' },
  info: { icon: Info, color: 'text-blue-600', bg: 'border-blue-300 bg-blue-50' },
};

const TYPE_ICONS = {
  low_stock: Package,
  oc_sin_factura: FileText,
  import_error: Database,
  lote_vb_resultado: ClipboardCheck,
  nc_pendientes: AlertTriangle,
  // Iter 313.j.26 (Renzo, feb-2026): NC de rendiciones sin factura aplicable en Alegra.
  nc_sin_factura_aplicable: AlertTriangle,
};

const formatDate = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('es-CL', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
};

export default function Notificaciones() {
  const [configs, setConfigs] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [activeTab, setActiveTab] = useState('config');
  const [editingRecipients, setEditingRecipients] = useState({});
  const [newRecipient, setNewRecipient] = useState({});

  const loadConfigs = useCallback(async () => {
    try {
      const res = await notificationsApi.getConfig();
      setConfigs(res.data.configs || []);
    } catch (err) { console.error('caught:', err); }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const res = await notificationsApi.getHistory(30);
      setHistory(res.data.history || []);
    } catch (err) { console.error('caught:', err); }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([loadConfigs(), loadHistory()]);
      setLoading(false);
    };
    load();
  }, [loadConfigs, loadHistory]);

  const handleToggle = async (alertType, field, value) => {
    const cfg = configs.find(c => c.alert_type === alertType);
    if (!cfg) return;
    const updated = {
      alert_type: alertType,
      enabled: field === 'enabled' ? value : cfg.enabled,
      threshold: cfg.threshold,
      email_enabled: field === 'email_enabled' ? value : cfg.email_enabled,
      email_recipients: cfg.email_recipients || [],
    };
    setSaving(true);
    try {
      await notificationsApi.updateConfig(updated);
      await loadConfigs();
      toast.success('Configuración actualizada');
    } catch {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleThresholdChange = async (alertType, newThreshold) => {
    const cfg = configs.find(c => c.alert_type === alertType);
    if (!cfg) return;
    const updated = {
      alert_type: alertType,
      enabled: cfg.enabled,
      threshold: parseInt(newThreshold) || 0,
      email_enabled: cfg.email_enabled,
      email_recipients: cfg.email_recipients || [],
    };
    setSaving(true);
    try {
      await notificationsApi.updateConfig(updated);
      await loadConfigs();
      toast.success('Umbral actualizado');
    } catch {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const addRecipient = async (alertType) => {
    const email = (newRecipient[alertType] || '').trim();
    if (!email || !email.includes('@')) { toast.error('Email inválido'); return; }
    const cfg = configs.find(c => c.alert_type === alertType);
    if (!cfg) return;
    const recipients = [...(cfg.email_recipients || [])];
    if (recipients.includes(email)) { toast.error('Email ya agregado'); return; }
    recipients.push(email);
    const updated = { alert_type: alertType, enabled: cfg.enabled, threshold: cfg.threshold, email_enabled: cfg.email_enabled, email_recipients: recipients };
    try {
      await notificationsApi.updateConfig(updated);
      await loadConfigs();
      setNewRecipient(prev => ({ ...prev, [alertType]: '' }));
      toast.success('Destinatario agregado');
    } catch { toast.error('Error al agregar destinatario'); }
  };

  const removeRecipient = async (alertType, email) => {
    const cfg = configs.find(c => c.alert_type === alertType);
    if (!cfg) return;
    const recipients = (cfg.email_recipients || []).filter(e => e !== email);
    const updated = { alert_type: alertType, enabled: cfg.enabled, threshold: cfg.threshold, email_enabled: cfg.email_enabled, email_recipients: recipients };
    try {
      await notificationsApi.updateConfig(updated);
      await loadConfigs();
      toast.success('Destinatario eliminado');
    } catch { toast.error('Error'); }
  };

  const runCheck = async () => {
    setChecking(true);
    try {
      const res = await notificationsApi.check();
      setAlerts(res.data.alerts || []);
      await loadHistory();
      if (res.data.total === 0) toast.success('No se encontraron alertas activas');
      else toast.warning(`Se encontraron ${res.data.total} alerta(s)`);
    } catch { toast.error('Error al verificar notificaciones'); }
    finally { setChecking(false); }
  };

  const sendEmails = async () => {
    setSending(true);
    try {
      const res = await notificationsApi.sendEmail();
      await loadHistory();
      if (res.data.emails_sent > 0) toast.success(`${res.data.emails_sent} email(s) enviado(s)`);
      else toast.info(res.data.message || 'No se enviaron emails');
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al enviar emails'); }
    finally { setSending(false); }
  };

  const sendTestEmail = async () => {
    if (!testEmail.includes('@')) { toast.error('Ingrese un email válido'); return; }
    setSendingTest(true);
    try {
      const res = await notificationsApi.testEmail(testEmail);
      if (res.data.success) toast.success('Email de prueba enviado');
      else toast.error(res.data.message || 'Error al enviar');
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al enviar email de prueba'); }
    finally { setSendingTest(false); }
  };

  const clearHistory = async () => {
    try { await notificationsApi.clearHistory(); setHistory([]); toast.success('Historial limpiado'); }
    catch { toast.error('Error'); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div data-testid="notificaciones-page" className="space-y-6 pb-20">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bell className="w-6 h-6 text-amber-500" />
            Notificaciones
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Configure alertas y notificaciones por email</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="check-notifications-btn" variant="outline" size="sm" onClick={runCheck} disabled={checking}>
            {checking ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <Play className="w-4 h-4 mr-1" />}
            Verificar Ahora
          </Button>
          <Button data-testid="send-emails-btn" size="sm" onClick={sendEmails} disabled={sending} className="bg-blue-600 hover:bg-blue-700 text-white">
            {sending ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
            Enviar Emails
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-muted/50 p-1 rounded-lg w-fit border">
        {[
          { id: 'config', label: 'Configuración', icon: Settings2 },
          { id: 'alerts', label: `Alertas${alerts.length ? ` (${alerts.length})` : ''}`, icon: BellRing },
          { id: 'history', label: 'Historial', icon: History },
          { id: 'test', label: 'Prueba Email', icon: MailCheck },
        ].map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              data-testid={`tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-background text-foreground shadow-sm border'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Config Tab */}
      {activeTab === 'config' && (
        <div className="grid gap-4">
          {configs.map(cfg => {
            const meta = cfg.meta || {};
            const TypeIcon = TYPE_ICONS[cfg.alert_type] || Bell;
            const isExpanded = editingRecipients[cfg.alert_type];
            const isEventDriven = meta.is_event_driven;

            return (
              <Card key={cfg.alert_type} data-testid={`config-${cfg.alert_type}`}>
                <CardContent className="p-5 space-y-4">
                  {/* Header row */}
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className={`p-2.5 rounded-lg ${cfg.enabled ? 'bg-amber-100 text-amber-600' : 'bg-muted text-muted-foreground'}`}>
                        <TypeIcon className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold">{meta.label || cfg.alert_type}</h3>
                        <p className="text-xs text-muted-foreground mt-0.5">{meta.description || ''}</p>
                        {isEventDriven && cfg.enabled && (
                          <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full border border-blue-200">
                            <BellRing className="w-3 h-3" /> Se envía automáticamente al ocurrir el evento
                          </span>
                        )}
                      </div>
                    </div>
                    <label data-testid={`toggle-${cfg.alert_type}`} className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" checked={cfg.enabled} onChange={e => handleToggle(cfg.alert_type, 'enabled', e.target.checked)}
                        className="sr-only peer" disabled={saving} />
                      <div className="w-9 h-5 bg-gray-300 peer-checked:bg-emerald-500 rounded-full transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4 after:shadow-sm"></div>
                    </label>
                  </div>

                  {cfg.enabled && (
                    <>
                      {/* Threshold - hide for event-driven types */}
                      {!isEventDriven && (
                        <div className="flex items-center gap-3 pl-12">
                          <label className="text-xs text-muted-foreground whitespace-nowrap">{meta.threshold_label || 'Umbral'}:</label>
                          <Input data-testid={`threshold-${cfg.alert_type}`} type="number" min="0"
                            value={cfg.threshold}
                            onChange={e => { const val = e.target.value; setConfigs(prev => prev.map(c => c.alert_type === cfg.alert_type ? { ...c, threshold: parseInt(val) || 0 } : c)); }}
                            onBlur={e => handleThresholdChange(cfg.alert_type, e.target.value)}
                            className="w-24 h-8 text-sm" />
                          <span className="text-xs text-muted-foreground">{meta.threshold_unit || ''}</span>
                        </div>
                      )}

                      {/* Email toggle */}
                      <div className="flex items-center justify-between pl-12">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-muted-foreground" />
                          <span className="text-xs">Enviar por email</span>
                        </div>
                        <label data-testid={`email-toggle-${cfg.alert_type}`} className="relative inline-flex items-center cursor-pointer">
                          <input type="checkbox" checked={cfg.email_enabled} onChange={e => handleToggle(cfg.alert_type, 'email_enabled', e.target.checked)}
                            className="sr-only peer" disabled={saving} />
                          <div className="w-9 h-5 bg-gray-300 peer-checked:bg-blue-500 rounded-full transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4 after:shadow-sm"></div>
                        </label>
                      </div>

                      {/* Recipients */}
                      {cfg.email_enabled && (
                        <div className="pl-12 space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-muted-foreground">Destinatarios ({(cfg.email_recipients || []).length})</span>
                            <button data-testid={`toggle-recipients-${cfg.alert_type}`}
                              onClick={() => setEditingRecipients(prev => ({ ...prev, [cfg.alert_type]: !prev[cfg.alert_type] }))}
                              className="text-xs text-blue-600 hover:text-blue-500 font-medium">
                              {isExpanded ? 'Ocultar' : 'Editar'}
                            </button>
                          </div>

                          {/* Quick view */}
                          {!isExpanded && (cfg.email_recipients || []).length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {(cfg.email_recipients || []).map(email => (
                                <span key={email} className="text-xs bg-muted px-2 py-0.5 rounded-full">{email}</span>
                              ))}
                            </div>
                          )}

                          {/* Expanded editor */}
                          {isExpanded && (
                            <div className="space-y-2 bg-muted/50 rounded-lg p-3 border">
                              {(cfg.email_recipients || []).map(email => (
                                <div key={email} className="flex items-center justify-between bg-background rounded px-3 py-1.5 border">
                                  <span className="text-xs">{email}</span>
                                  <button data-testid={`remove-recipient-${cfg.alert_type}-${email}`}
                                    onClick={() => removeRecipient(cfg.alert_type, email)}
                                    className="text-red-500 hover:text-red-400 p-0.5">
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                              <div className="flex gap-2">
                                <Input data-testid={`new-recipient-${cfg.alert_type}`} type="email" placeholder="email@ejemplo.com"
                                  value={newRecipient[cfg.alert_type] || ''}
                                  onChange={e => setNewRecipient(prev => ({ ...prev, [cfg.alert_type]: e.target.value }))}
                                  onKeyDown={e => e.key === 'Enter' && addRecipient(cfg.alert_type)}
                                  className="h-8 text-xs flex-1" />
                                <Button data-testid={`add-recipient-${cfg.alert_type}`} size="sm" variant="outline"
                                  onClick={() => addRecipient(cfg.alert_type)} className="h-8 px-2">
                                  <Plus className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          )}

                          {!isExpanded && (cfg.email_recipients || []).length === 0 && (
                            <p className="text-xs text-muted-foreground italic">Sin destinatarios configurados</p>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {/* Last updated */}
                  {cfg.updated_at && (
                    <p className="text-[10px] text-muted-foreground pl-12">
                      Actualizado: {formatDate(cfg.updated_at)} por {cfg.updated_by || '-'}
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Alerts Tab */}
      {activeTab === 'alerts' && (
        <div className="space-y-4">
          {alerts.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
                <p className="font-medium">Sin alertas activas</p>
                <p className="text-xs text-muted-foreground mt-1">Haga clic en "Verificar Ahora" para ejecutar una verificación</p>
              </CardContent>
            </Card>
          ) : (
            alerts.map((alert, idx) => {
              const sev = SEVERITY_STYLE[alert.severity] || SEVERITY_STYLE.info;
              const Icon = sev.icon;
              const TypeIcon = TYPE_ICONS[alert.alert_type] || Bell;
              return (
                <Card key={idx} data-testid={`alert-card-${alert.alert_type}`} className={`border ${sev.bg}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${sev.color}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <TypeIcon className="w-4 h-4 text-muted-foreground" />
                          <h3 className="text-sm font-semibold">{alert.title}</h3>
                        </div>
                        <p className="text-xs text-muted-foreground">{alert.message}</p>
                        {alert.details && alert.details.length > 0 && (
                          <div className="mt-3 max-h-48 overflow-auto rounded border">
                            <table className="w-full text-xs">
                              <tbody>
                                {alert.details.slice(0, 10).map((item, i) => (
                                  <tr key={i} className="border-b last:border-0">
                                    <td className="py-1.5 px-2">{item.codigo || item.numero_oc || item.source || '-'}</td>
                                    <td className="py-1.5 px-2 text-muted-foreground">{item.descripcion || item.proveedor_nombre || item.error || '-'}</td>
                                    <td className="py-1.5 px-2 text-right text-muted-foreground">
                                      {item.cantidad !== undefined ? item.cantidad : item.total_neto ? `$${item.total_neto.toLocaleString('es-CL')}` : formatDate(item.timestamp)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                        {alert.email_enabled && (
                          <div className="mt-2 flex items-center gap-1 text-[10px] text-blue-600">
                            <Mail className="w-3 h-3" />
                            Email habilitado ({(alert.email_recipients || []).length} destinatarios)
                          </div>
                        )}
                      </div>
                      <span className="text-[10px] text-muted-foreground shrink-0">{formatDate(alert.timestamp)}</span>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-sm font-medium">Historial de Notificaciones</h2>
            {history.length > 0 && (
              <Button data-testid="clear-history-btn" variant="ghost" size="sm" onClick={clearHistory}
                className="text-red-500 hover:text-red-400 hover:bg-red-50">
                <Trash2 className="w-4 h-4 mr-1" /> Limpiar
              </Button>
            )}
          </div>
          {history.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <History className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Sin historial de notificaciones</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {history.map((h, i) => (
                <div key={i} className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg border">
                  {h.type === 'email_sent' ? (
                    <MailCheck className="w-4 h-4 text-emerald-500 shrink-0" />
                  ) : (
                    <Bell className="w-4 h-4 text-muted-foreground shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    {h.type === 'email_sent' ? (
                      <p className="text-xs">
                        Email enviado: <span className="text-emerald-600 font-medium">{h.alert_type}</span> a {(h.recipients || []).join(', ')}
                        {h.detail && <span className="text-muted-foreground ml-1">- {h.detail}</span>}
                      </p>
                    ) : (
                      <p className="text-xs">
                        Verificación: <span className="text-amber-600 font-medium">{h.alerts_found}</span> alerta(s) encontrada(s)
                        {h.alert_types && h.alert_types.length > 0 && (
                          <span className="text-muted-foreground"> ({h.alert_types.join(', ')})</span>
                        )}
                      </p>
                    )}
                    <p className="text-[10px] text-muted-foreground">{h.checked_by || h.sent_by || '-'}</p>
                  </div>
                  <span className="text-[10px] text-muted-foreground shrink-0">{formatDate(h.checked_at || h.sent_at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Test Email Tab */}
      {activeTab === 'test' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <MailCheck className="w-5 h-5 text-blue-500" />
              Enviar Email de Prueba
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-xs text-muted-foreground">
              Envía un email de prueba para verificar que el sistema de notificaciones puede enviar correos correctamente.
            </p>
            <div className="flex gap-2">
              <Input data-testid="test-email-input" type="email" placeholder="ingrese email de prueba"
                value={testEmail} onChange={e => setTestEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && sendTestEmail()} className="max-w-sm" />
              <Button data-testid="send-test-email-btn" onClick={sendTestEmail} disabled={sendingTest || !testEmail}
                className="bg-blue-600 hover:bg-blue-700 text-white">
                {sendingTest ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                Enviar Prueba
              </Button>
            </div>
            <div className="bg-muted/50 rounded-lg p-3 text-xs text-muted-foreground space-y-1 border">
              <p className="font-medium text-foreground">Requisitos:</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>Microsoft Graph API configurado (MS_CLIENT_ID, MS_TENANT_ID, MS_CLIENT_SECRET)</li>
                <li>Email del remitente configurado (MS_SENDER_EMAIL)</li>
                <li>Permisos de Mail.Send en Azure AD</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
